import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import styled from 'styled-components';
import { connect } from 'react-redux';

import { Show } from '../../show';

import { getTimerTooltipText } from '..';
import Timer from '../../timer';
import Tooltip from '../../tooltip';

import ZLogoPath from '../assets/z-logo.png';

import { useWindowSize, useScrollPosition, useOnlineStatus } from '../../../lib/hooks';

import ColumnContainer from '../../layouts/components/column-container';
import FluidContainer from '../../layouts/components/fluid-container';
import Skeleton from '../../skeleton';
import PauseButton from '../../pause-button';

export const Logo = styled.img`
    position: absolute;
    ${(props) => props.isLogoClickable && 'cursor: pointer;'}
`;

export const BookAndTitleContainer = styled.div`
    display: flex;
    height: ${(props) => (props.scrolled ? '0px' : '70px')};
    visibility: ${(props) => (props.scrolled ? 'hidden' : 'visible')};
    opacity: ${(props) => (props.scrolled ? 0 : 1)};
    flex-direction: column;
    overflow: hidden;
    transition: all 500ms ease;
    padding-right: 16px;
    padding-top: ${(props) => (props.scrolled ? 0 : '8px')};
`;

const Divider = styled.div`
    height: 8px;
    border-bottom: ${(props) => (props.scrolled ? 0 : 1)}px solid var(--gray200);
`;

const AuthorsOrTitle = styled.span`
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
    font-weight: normal;

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const Book = styled.span`
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0;
    font-weight: 600;

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const HomeworkDetailsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 8px 8px 0;
`;

const TypeAndNumberOfExercisesContainer = styled.div`
    display: flex;
    align-items: center;
    height: 28px;
    border-radius: 14px;
    padding: 0 12px;
    background-color: var(--color-white);
    border: 1px solid var(--gray200);
    box-sizing: border-box;
`;

const Type = styled.span`
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
    font-weight: 600;
    margin-right: 4px;
`;

export const NumberOfExercises = styled.span`
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
    font-weight: normal;
    white-space: nowrap;
`;

const TimerTooltip = styled(Tooltip)`
    white-space: nowrap;
    color: var(--color-white);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.16px;
    line-height: 20px;
    text-transform: uppercase;
`;

const TimerWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const MobileHeader = ({
    applicationStatus = {},
    authors,
    book,
    finished,
    homeworkStatus = {},
    homeworkTime,
    isLoading,
    numberOfExercises = 0,
    paused,
    profile,
    showModal,
    started,
    status,
    terminateHomework,
    type,
    homework,
    assignment,
    updateStatusError,
    redirect,
}) => {
    const windowSize = useWindowSize();
    const { y: scrollY } = useScrollPosition();
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const node = useRef();
    const onlineStatus = useOnlineStatus();

    const handleClick = (e) => {
        if (node.current && !node.current.contains(e.target)) {
            setTooltipVisible(false);
        }
    };

    useEffect(() => {
        // add when mounted
        document.addEventListener('mousedown', handleClick);
        // return function to be called when unmounted
        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);

    const creator = homework.ugc ? `${homework.creator?.firstName} ${homework.creator?.lastName}` : authors;
    const importantText = homework.ugc ? (assignment.title ? assignment.title : homework.publicTitle) : book;
    const secondaryImportantText = !homework.ugc ? (assignment.title ? assignment.title : homework.publicTitle) : '';
    const isLogoClickable = !isLoading && !profile.isEmbedded;

    const exercisesNumberLabel = () => {
        if (profile.sections) return 'quiz';
        else return numberOfExercises === 1 ? 'esercizio' : 'esercizi';
    };

    return (
        <ColumnContainer backgroundColor="surface-grey-050" boxShadow="shadow-01dp" zIndex={3}>
            <Logo src={ZLogoPath} isLogoClickable={isLogoClickable} height={44} onClick={isLogoClickable ? redirect : null} />

            <FluidContainer flexDirection="column" paddingLeft={50} paddingRight={0} width="calc(100% - 66px)" minHeight="44px">
                {applicationStatus.completed && !homeworkStatus.error && (
                    <>
                        <BookAndTitleContainer scrolled={scrollY >= 60} style={{ maxWidth: '100%' }}>
                            <Skeleton isLoading={isLoading} items={[{ width: 70 }, {}]}>
                                <AuthorsOrTitle dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(creator) }} />
                                <Book>{importantText}</Book>
                                <AuthorsOrTitle>{secondaryImportantText}</AuthorsOrTitle>
                            </Skeleton>
                            <Divider scrolled={scrollY >= 60} />
                        </BookAndTitleContainer>
                        <HomeworkDetailsContainer>
                            <Skeleton isLoading={isLoading} items={[{ height: 'regular' }]} width={120}>
                                <TypeAndNumberOfExercisesContainer>
                                    <Show when={windowSize.innerWidth > 370}>
                                        <Type>{type}</Type>
                                    </Show>
                                    <NumberOfExercises>{`${numberOfExercises} ${exercisesNumberLabel()}`}</NumberOfExercises>
                                </TypeAndNumberOfExercisesContainer>
                            </Skeleton>

                            <Skeleton isLoading={isLoading} items={[{ height: 'regular' }]} width={120}>
                                <TimerWrapper>
                                    {status && Number.isInteger(status.remainingTime) && (
                                        <div
                                            id="timer-tooltip-container-id"
                                            style={{ position: 'relative' }}
                                            ref={node}
                                            onClick={() => {
                                                setTooltipVisible(!tooltipVisible);
                                            }}
                                        >
                                            <Timer terminateHomework={terminateHomework} />
                                            {tooltipVisible && (
                                                <TimerTooltip content={getTimerTooltipText(status, homeworkTime)} top="-5px" right="32px" />
                                            )}
                                        </div>
                                    )}
                                    <Show when={profile.pauseTest && started && !finished && !paused}>
                                        <PauseButton disabled={!onlineStatus || updateStatusError} showModal={showModal} />
                                    </Show>
                                </TimerWrapper>
                            </Skeleton>
                        </HomeworkDetailsContainer>
                    </>
                )}
            </FluidContainer>
        </ColumnContainer>
    );
};

MobileHeader.propTypes = {
    type: PropTypes.string,
    status: PropTypes.object,
    started: PropTypes.bool,
    showModal: PropTypes.func,
    profile: PropTypes.object,
    paused: PropTypes.bool,
    numberOfExercises: PropTypes.number,
    isLoading: PropTypes.bool,
    homeworkTime: PropTypes.number,
    homeworkStatus: PropTypes.object,
    finished: PropTypes.bool,
    book: PropTypes.string,
    authors: PropTypes.string,
    applicationStatus: PropTypes.object,
    homework: PropTypes.object,
    assignment: PropTypes.object,
    updateStatusError: PropTypes.bool,
    redirect: PropTypes.func,
    terminateHomework: PropTypes.func,
};

const stateToProps = (state) => {
    return {
        finished: state.status.finished,
        paused: state.status.paused,
        profile: state.application,
        started: state.status.started,
        status: state.status,
        homework: state.homework,
        assignment: state.assignment,
        updateStatusError: !!state.status.httpStatus.errorbody,
    };
};

export default connect(stateToProps)(MobileHeader);
